"use client";

import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { ChartContext, type TooltipData } from "../../../context/ChartContext";
import { getYFormatSettings, type YFormatType } from "../../../utils/charts";
import { getAdaptiveTimeFormat } from "../../../utils/timeFormat";

interface ChartTooltipProps {
  data: TooltipData | null;
  formatter: YFormatType;
  /** Optional label to display instead of formatting xValue (for categorical charts) */
  label?: string;
  /** Optional category labels override (for standalone usage without ChartContext) */
  categoryLabels?: Record<string, string>;
}

/**
 * ChartTooltip
 *
 * Interactive tooltip component for charts.
 * Displays formatted data values at hover position with automatic positioning and animations.
 * Uses the main formatting system for consistency with Kpi, StatList, and DataTable.
 *
 * Can be used standalone (with label prop) or within ChartContext (for time-series charts).
 */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  data,
  formatter,
  label,
  categoryLabels: categoryLabelsProp,
}) => {
  // Try to get context values, but don't require them
  const context = React.useContext(ChartContext);
  const categoryLabels = categoryLabelsProp ?? context?.categoryLabels;
  const xScale = context?.xScale;
  // Chart-local bounds are the source of truth for tooltip flip decisions.
  // `data.x` / `data.y` are in chart-local coords (the tooltip is absolutely
  // positioned inside the chart container), so we compare against the chart's
  // own width/height — not viewport dimensions. Using viewport here caused
  // tooltips to extend past the chart card when the chart was inset from the
  // viewport edge (BOLT-1102 follow-up).
  const chartWidth = context?.width;
  const chartHeight = context?.height;
  const formatSettings = getYFormatSettings(formatter);
  const formatSeriesValue = (series: { value: number; baselineValue?: number }) => {
    if (series.baselineValue === undefined) return formatSettings.format(series.value);
    const lo = Math.min(series.baselineValue, series.value);
    const hi = Math.max(series.baselineValue, series.value);
    return `${formatSettings.format(lo)} – ${formatSettings.format(hi)}`;
  };
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({
    x: 0,
    y: 0,
    translate: "translate(-50%, -100%)",
  });

  React.useEffect(() => {
    if (!data || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    // Fall back to viewport when ChartContext isn't available (standalone
    // usage with categorical charts).
    const rightBound = chartWidth ?? window.innerWidth;
    const bottomBound = chartHeight ?? window.innerHeight;

    let x = data.x;
    let y = data.y;

    // Pick the side that fits, regardless of the initial `position` hint.
    // The hint reflects the producer's best guess at hover time (e.g.
    // "the data point is past chart midpoint, prefer left"), but the
    // actual decision must account for the *measured* tooltip width —
    // wide event tooltips can still overflow on the preferred side.
    const OFFSET = 16;
    const fitsRight = data.x + rect.width + OFFSET <= rightBound;
    const fitsLeft = data.x - rect.width - OFFSET >= 0;

    let preferLeft: boolean;
    if (data.position === "left") {
      // Producer wants left; honor it unless it doesn't fit AND right does.
      preferLeft = fitsLeft || !fitsRight;
    } else {
      // Producer wants right; honor it unless it doesn't fit AND left does.
      preferLeft = !fitsRight && fitsLeft;
    }

    let translate = preferLeft ? "translate(calc(-100% - 16px), -50%)" : "translate(16px, -50%)";

    // Final-resort clamp: if neither side fits within the chart bounds (very
    // wide tooltip or very narrow chart), pin the tooltip flush against the
    // nearer edge by anchoring it to that edge and using the corresponding
    // translate. This guarantees the tooltip never extends past the chart
    // container even when no side has enough room for the natural offset.
    if (!fitsRight && !fitsLeft) {
      const distanceToRight = rightBound - data.x;
      const distanceToLeft = data.x;
      if (distanceToRight <= distanceToLeft) {
        // Anchor is closer to the right edge — pin tooltip flush against it.
        x = rightBound;
        translate = "translate(-100%, -50%)";
      } else {
        // Anchor is closer to the left edge — pin tooltip flush against it.
        x = 0;
        translate = "translate(0, -50%)";
      }
    }

    // Handle vertical positioning
    if (y - rect.height / 2 < 0) {
      y = rect.height / 2;
    } else if (y + rect.height / 2 > bottomBound) {
      y = bottomBound - rect.height / 2;
    }

    setPosition({ x, y, translate });
  }, [data?.x, data?.y, data?.position, chartWidth, chartHeight]);

  // Hover and camera gestures toggle data without unmounting this component.
  // Keep the hook order stable when the tooltip is hidden.
  if (!data) return null;

  // Get adaptive time format based on the chart's time range (only for temporal domains)
  // If label prop is provided, use it directly (for categorical charts)
  const domain = xScale?.domain();
  const hasTemporalDomain = domain && domain.length === 2 && domain.every((value) => value instanceof Date);
  const timeFormatter = hasTemporalDomain
    ? getAdaptiveTimeFormat({
        start: domain[0] as Date,
        end: domain[1] as Date,
      })
    : null;

  // Format the label based on whether we have a label prop, temporal data, or categorical data
  const formattedLabel = label
    ? label
    : timeFormatter && data.xValue instanceof Date
      ? timeFormatter(data.xValue)
      : String(data.xValue);

  return (
    <AnimatePresence>
      <motion.div
        ref={tooltipRef}
        role="tooltip"
        aria-live="polite"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="tooltip"
        className="pointer-events-none absolute z-50 rounded-lg bg-background-surface p-4 text-sm shadow-xl ring-1 ring-border-default whitespace-nowrap"
        style={{
          left: position.x,
          top: position.y,
          transform: position.translate,
        }}
      >
        <div className="mb-1 font-medium text-text-primary">{formattedLabel}</div>

        {data.isEvent ? (
          // Event marker tooltip with series data + event info
          <div className="space-y-2">
            {/* Render underlying series data (all except the last one which is the event) */}
            {data.series.length > 1 && (
              <ul className="list-none p-0 border-b border-border-default pb-2">
                {data.series.slice(0, -1).map((series) => (
                  <li key={series.label} className="py-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-text-primary">{series.label}:</span>
                      <span className="text-text-secondary">{formatSeriesValue(series)}</span>
                    </div>
                    {series.category && (
                      <div className="mt-0.5 flex items-center gap-2">
                        <div
                          className="h-3 w-3"
                          style={{
                            backgroundColor: series.categoryColor || series.color,
                            borderRadius: "1px",
                          }}
                        />
                        <span className="text-sm text-text-secondary">
                          {categoryLabels?.[series.category] || series.category}
                        </span>
                      </div>
                    )}
                    {series.extraTooltipRows && series.extraTooltipRows.length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {series.extraTooltipRows.map((row) => (
                          <div key={row.label} className="flex items-center gap-2 text-sm text-text-secondary">
                            <span>{row.label}:</span>
                            <span className="font-medium text-text-primary">{row.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Render event information (last series item, or expanded event items when provided) */}
            {(() => {
              const eventSeries = data.series[data.series.length - 1];
              const eventItems = data.eventItems?.length
                ? data.eventItems
                : [
                    {
                      label: eventSeries.label,
                      color: eventSeries.color,
                      metadata: data.eventMetadata,
                    },
                  ];

              return (
                <div className="space-y-2">
                  {eventItems.map((item, itemIndex) => (
                    <div key={`${item.label}-${itemIndex}`}>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="font-medium text-text-primary">{item.label}</span>
                      </div>
                      {item.metadata && Object.keys(item.metadata).length > 0 && (
                        <div className="ml-4 mt-1 space-y-0.5 text-xs text-text-secondary">
                          {Object.entries(item.metadata).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              <span className="capitalize">{key}:</span>
                              <span className="font-medium">{value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ) : (
          // Data point tooltip
          <ul className="list-none p-0">
            {data.series.map((series) => (
              <li key={series.label} className="py-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text-primary">{series.label}:</span>
                  <span className="text-text-secondary">{formatSeriesValue(series)}</span>
                </div>
                {series.category && (
                  <div className="mt-0.5 flex items-center gap-2">
                    <div
                      className="h-3 w-3"
                      style={{
                        backgroundColor: series.categoryColor || series.color,
                        borderRadius: "1px",
                      }}
                    />
                    <span className="text-sm text-text-secondary">
                      {categoryLabels?.[series.category] || series.category}
                    </span>
                  </div>
                )}
                {series.extraTooltipRows && series.extraTooltipRows.length > 0 && (
                  <div className="mt-0.5 space-y-0.5">
                    {series.extraTooltipRows.map((row) => (
                      <div key={row.label} className="flex items-center gap-2 text-sm text-text-secondary">
                        <span>{row.label}:</span>
                        <span className="font-medium text-text-primary">{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
